import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Box, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import toast from 'react-hot-toast';
import { StyledTabs, StyledTab } from '../../../styles/StyledComponents';
import { useEventsListQuery } from '../../../api/use-events-list-query';
import { useArchiveEventMutation } from '../../../api/use-archive-event-mutation';
import { useSubmitPartnerEventMutation } from '../../../api/use-submit-partner-event-mutation';
import { fetchEventDetail } from '../../../api/use-event-query';
import { canShowPartnerEventEdit, useEventsScope } from '../../../contexts/EventsScopeContext';
import { canArchivePartnerEvent } from './partnerEventArchive';
import { EVENT_VIEW_MODES } from '../../Events/eventsListData';
import EventsListToolbar from '../../Events/widgets/EventsListToolbar';
import ArchiveEventDialog from './widgets/ArchiveEventDialog';
import EventsGridView from '../../Events/widgets/EventsGridView';
import PartnerEventsListTable from './widgets/PartnerEventsListTable';
import EventSubmissionSuccessModal from './widgets/EventSubmissionSuccessModal';
import SubmitEventDialog from './widgets/SubmitEventDialog';
import PortalPaginationControls from '../../layout/PortalPaginationControls';
import {
  PARTNER_EVENT_STATUS_TABS,
  PARTNER_EVENTS_PER_PAGE,
  buildPartnerEventsListQueryParams,
  isPartnerViewOnlyTab,
} from './partnerEventsListData';
import { buildSubmittedEventSummary } from './submittedEventSummary';

const ListPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(2),
  boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.06)',
}));

const MAX_API_LIMIT = 100;
const SEARCH_DEBOUNCE_MS = 400;

const PartnerEventsListSection = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { routes, features, api, queryKeys } = useEventsScope();
  const [archiveEventId, setArchiveEventId] = useState(null);
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [viewMode, setViewMode] = useState(EVENT_VIEW_MODES.LIST);
  const [page, setPage] = useState(1);
  const [submitEventId, setSubmitEventId] = useState(null);
  const [submittedEventSummary, setSubmittedEventSummary] = useState(null);

  const archiveEventMutation = useArchiveEventMutation();
  const submitEventMutation = useSubmitPartnerEventMutation();

  const limit = Math.min(PARTNER_EVENTS_PER_PAGE, MAX_API_LIMIT);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [search]);

  const queryParams = useMemo(
    () =>
      buildPartnerEventsListQueryParams({
        page,
        limit,
        tabIndex: tab,
        search: debouncedSearch,
        categoryType: typeFilter,
      }),
    [page, limit, tab, debouncedSearch, typeFilter],
  );

  const { data, isLoading, isFetching, isError, error } = useEventsListQuery(queryParams);

  const events = data?.events ?? [];
  const apiTotal = data?.total ?? 0;
  const totalPages = data?.totalPages ?? Math.max(1, Math.ceil(apiTotal / limit));
  const currentPage = Math.min(page, totalPages);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setPage(1);
  };

  const handleTabChange = (_, newValue) => {
    setTab(newValue);
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleTypeFilterChange = (e) => {
    setTypeFilter(e.target.value);
    setPage(1);
  };

  const viewOnlyRowActions = isPartnerViewOnlyTab(tab);

  const handleViewEvent = (id) => {
    navigate(routes.detail(id));
  };

  const handleEditEvent = (id) => {
    const event = events.find((item) => item.id === id);
    if (event && !canShowPartnerEventEdit(event, features)) {
      toast.error('Event cannot be edited while it is under moderation review.');
      return;
    }
    navigate(routes.edit(id));
  };

  const handleArchiveRequest = (id) => {
    const event = events.find((item) => item.id === id);
    if (!event || !canArchivePartnerEvent(event)) {
      return;
    }
    setArchiveEventId(id);
  };

  const handleSubmitRequest = (id) => {
    setSubmitEventId(id);
  };

  const handleSubmitClose = () => {
    if (!submitEventMutation.isPending) {
      setSubmitEventId(null);
    }
  };

  const handleSubmitConfirm = () => {
    if (!submitEventId || submitEventMutation.isPending) return;

    const idToSubmit = submitEventId;
    const targetEvent = events.find((item) => item.id === idToSubmit);

    submitEventMutation.mutate(idToSubmit, {
      onSuccess: async (payload) => {
        setSubmitEventId(null);
        if (payload?.contentModerationPassed === false) {
          toast.error(
            payload?.moderationFeedback || payload?.message || 'Event failed content moderation',
          );
          return;
        }

        let summary = buildSubmittedEventSummary(payload, targetEvent);
        if (summary.price === '—' && idToSubmit) {
          try {
            const detail = await queryClient.fetchQuery({
              queryKey: queryKeys.detail(idToSubmit),
              queryFn: () => fetchEventDetail(api, idToSubmit),
            });
            summary = buildSubmittedEventSummary(detail, targetEvent);
          } catch {
            // Keep summary built from submit payload when detail fetch fails.
          }
        }
        setSubmittedEventSummary(summary);
      },
      onError: (err) => {
        toast.error(err?.message || 'Failed to submit event');
      },
    });
  };

  const handleArchiveClose = () => {
    setArchiveEventId(null);
  };

  const handleArchiveConfirm = () => {
    if (!archiveEventId || archiveEventMutation.isPending) return;

    const idToArchive = archiveEventId;

    archiveEventMutation.mutate(idToArchive, {
      onSuccess: () => {
        setArchiveEventId(null);
        toast.success('Event archived successfully');
      },
      onError: (err) => {
        toast.error(err?.message || 'Failed to archive event');
      },
    });
  };

  const eventToArchive = events.find((e) => e.id === archiveEventId);
  const eventToSubmit = events.find((e) => e.id === submitEventId);
  const showEmpty = !isLoading && !isError && events.length === 0;
  const submittingEventId = submitEventMutation.isPending ? submitEventMutation.variables : null;

  return (
    <ListPanel>
      <StyledTabs
        value={tab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        {PARTNER_EVENT_STATUS_TABS.map(({ label }) => (
          <StyledTab key={label} label={label} />
        ))}
      </StyledTabs>

      <EventsListToolbar
        search={search}
        onSearchChange={handleSearchChange}
        typeFilter={typeFilter}
        onTypeFilterChange={handleTypeFilterChange}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
      />

      {isLoading ? (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress sx={{ color: 'primary.main' }} />
        </Box>
      ) : isError ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body1" color="error">
            {error?.message || 'Failed to load events.'}
          </Typography>
        </Box>
      ) : showEmpty ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No events found.
          </Typography>
        </Box>
      ) : viewMode === EVENT_VIEW_MODES.GRID ? (
        <EventsGridView
          events={events}
          onEventClick={handleViewEvent}
          onView={handleViewEvent}
          onEdit={viewOnlyRowActions ? undefined : handleEditEvent}
          onArchive={viewOnlyRowActions ? undefined : handleArchiveRequest}
          onSubmit={viewOnlyRowActions ? undefined : handleSubmitRequest}
          submittingEventId={submittingEventId}
          features={features}
          viewOnlyRowActions={viewOnlyRowActions}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          <PartnerEventsListTable
            events={events}
            onView={handleViewEvent}
            onEdit={viewOnlyRowActions ? undefined : handleEditEvent}
            onArchive={viewOnlyRowActions ? undefined : handleArchiveRequest}
            onSubmit={viewOnlyRowActions ? undefined : handleSubmitRequest}
            submittingEventId={submittingEventId}
            features={features}
            viewOnlyRowActions={viewOnlyRowActions}
            containerSx={{
              maxHeight: 'calc(100vh - 360px)',
              minHeight: 0,
              overflow: 'auto',
              borderRadius: 0,
              boxShadow: 'none',
            }}
          />
          {!isLoading && !isError && apiTotal > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.5,
                borderTop: '1px solid',
                borderTopColor: 'divider',
                bgcolor: 'background.paper',
                flexShrink: 0,
              }}
            >
              <PortalPaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                isLoading={isFetching}
                onPageChange={setPage}
              />
            </Box>
          )}
        </Box>
      )}

      <ArchiveEventDialog
        open={Boolean(archiveEventId)}
        event={eventToArchive}
        archiving={archiveEventMutation.isPending}
        onClose={handleArchiveClose}
        onConfirm={handleArchiveConfirm}
      />
      <SubmitEventDialog
        open={Boolean(submitEventId)}
        event={eventToSubmit}
        submitting={submitEventMutation.isPending}
        onClose={handleSubmitClose}
        onConfirm={handleSubmitConfirm}
      />
      <EventSubmissionSuccessModal
        open={Boolean(submittedEventSummary)}
        eventSummary={submittedEventSummary}
        onGoToEvents={() => setSubmittedEventSummary(null)}
      />
    </ListPanel>
  );
};

export default PartnerEventsListSection;
